import { BehaviorSubject, Observable } from 'rxjs';
import { FormGroup, ValidatorFn } from '@angular/forms';

import { AppFeatures } from '../app.features';
import { EventEmitter } from '@angular/core';
import { FormOption } from '../services/forms.service';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

export interface ChildPopupPaneComponent {
  formGroupChange?: EventEmitter<FormGroup>;
  componentDataChange?: EventEmitter<any>;
  ngOnInit?(): void;
  formGroup?: FormGroup;
  createFormGroup?: () => void;
  // ... any other common properties or methods
}

type AtLeastOne<T, U extends keyof T = keyof T> = {
  [K in U]: Required<Pick<T, K>> & Partial<Omit<T, K>>;
}[U];

/** define type that eather of the properties is required */
export type FormProperty = AtLeastOne<
  FormControlItem,
  'propertyName' | 'propertyNames'
>;

export interface FormTabItem {
  name: string;
  label: string;
  active?: boolean;
}

export interface FormConfiguration {
  tabs: FormTab[];
}

export interface FormTab {
  tabIndex: number;
  name: string;
  key: string; // Unique key for the tab
  label?: string;
  icon?: IconProp;
  searchOption?: boolean; // Option to enable search
  formPanelsClass?: string; // Optional style for form-panels container div
  panels: FormPanel[];
  countErrors?: number;
  availableInFormModes?: FormMode[];
  userPermission?: AppFeatures;
  userAccessLevel?: number; // User level required to access this tab
}

export interface FormPanel {
  panelName: string;
  panelLabel: string;
  panelClass?: string;
  layoutConfig?: LayoutConfig; // Configuration for flex layout
  items?: FormProperty[]; // Form controls within this panel
  panels?: FormPanel[]; // Nested child panels
  availableInFormModes?: FormMode[];
  userPermission?: AppFeatures;
  userAccessLevel?: number; // User level required to access this panel
}

export interface PropertyGroup {
  key: string;
  style?: string;
}

export enum FormMode {
  Create = 'create',
  Edit = 'edit',
}

export interface LayoutConfig {
  direction: 'row' | 'column'; // Flex direction
  justifyContent?:
    | 'flex-start'
    | 'center'
    | 'flex-end'
    | 'space-between'
    | 'space-around';
  alignItems?: 'flex-start' | 'center' | 'flex-end' | 'stretch';
  flexWrap?: 'nowrap' | 'wrap';
  gap?: string; // CSS gap between items (e.g., '0.75rem')
  width?: string; // Width of the panel (e.g., '100%')
  height?: string; // Height of the panel (optional)
  padding?: string; // Padding of the panel (optional)
  border?: string; // Border of the panel (optional)
}

export interface FormControlItem {
  propertyName: string;
  propertyNameLanguages?: any;
  propertyNames: string[]; // additional property names to be created
  propertyLabel?: string;
  columnIndex?: number;
  propertyGroup?: PropertyGroup;
  propertyGroupCol?: number;
  propertyGroupRow?: number;
  controlType:
    | 'text'
    | 'textarea'
    | 'select'
    | 'autocomplete'
    | 'checkbox'
    | 'checkbox-array'
    | 'structured-checkbox-array'
    | 'date'
    | 'colorpicker'
    | 'colorpicker-array'
    | 'input-number'
    | 'toggle'
    | 'language-picker'
    | 'ds-picker'
    | 'dataobject-picker'
    | 'principal-picker'
    | 'toggle-button-group'
    | 'date-picker'
    | 'time-picker'
    | 'tag-input'
    | 'image-upload'
    | 'button'
    | 'progress-steps'
    | 'info-alert'
    | 'tabs'
    | 'color-palette'
    | 'image-cropper'
    | 'password'
    | 'hidden'
    | 'multiselect'
    | 'search'
    | 'component'
    | 'file-input'
    | 'file-upload'
    | 'chat-message'
    | 'slider'
    | 'date-range-picker';
  validators?: ValidatorFn | ValidatorFn[];
  valueOptions?: FormOption[];
  loading?: boolean;
  valueOptionsAsync?: Observable<any[]>;
  additionalConfig?: {
    [key: string]: any;
  };
  defaultValue?: any | (() => any);
  metricSymbol?: string;
  formChanger?: boolean; // if the property should change the form controll visibility or validity
  tabName?: string;
  tabIndex?: number;
  shouldDisplay: (formGroup: FormGroup) => boolean; // estimate if the property should be displayed
  shouldDisplayAsync: (formGroup: FormGroup) => Observable<boolean>; // estimate if the property should be displayed async
  validateBy?: (formGroup: FormGroup) => ValidatorFn[]; // function to validate the property by some condition
  shouldDisplaySubject?: BehaviorSubject<boolean>; // emits when the property should be displayed if set, otherwise the propery is always displayed
  inline?: boolean;
  inlineBreak?: boolean;
  action?: () => void;
  containerStyleProperties?: Object;
  compnentInstance?: any;
  userPermission?: AppFeatures;
  availableInFormModes?: FormMode[];
  userAccessLevel?: number; // User level required to access this control
  onChange?: (value: any, form: FormGroup) => void;
  isMassUpdate?: boolean;
}

/**
 * Result an editor's `saveViaMutation` resolves with, so the base can run the
 * shared success path (toast + onObjectCreated / onObjectUpdated) without the
 * NgRx form-state mechanism. See the form-editor base components.
 */
export interface FormSaveResult {
  object: any;
  isCreated: boolean;
}

export enum EditorFormEntity {
  Default = 'default',
  Alert = 'alert',
  Category = 'category',
  Company = 'company',
  Dashboard = 'dashboard',
  ExportForm = 'exportForm',
  Feed = 'feed',
  Role = 'role',
  User = 'user',
  Workspace = 'workspace',
  UserGroup = 'user-group',
  OutputSettings = 'output-settings',
  ExportTemplate = 'export-template',
}
